/**
 * Employee-facing benefits enrollment wizard.
 *
 * Route: /benefits/enroll
 *
 * Flow (one question per screen):
 *   1. Coverage  — Just me / +spouse / +children / +family
 *   2. Usage     — Rarely / Sometimes / Often
 *   3. Rx        — None / 1-2 / 3+
 *   4. Priority  — Lower monthly / Predictable / Best coverage
 *   5. Recommendations — top pick highlighted, side-by-side toggle
 *   6. Dependents (if coverage > just_me)
 *   7. Review + confirm
 *   8. "You're enrolled!" confirmation
 *
 * Step 4-5 reasoning is generated server-side from the answers (see
 * benefits_recommendation_service.py). The UI does NOT re-rank — it
 * just renders what the API gives back.
 *
 * Design ethos: keep it big and conversational. Open enrollment is a
 * stressful task and a clean step-by-step beats a single overwhelming
 * form. This page is what we demo to Chris on the CBIZ call.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Download,
  Heart,
  HelpCircle,
  Home,
  Pill,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Trash2,
  Users,
  Wallet,
} from "lucide-react";

import { formatCurrency } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  getMyEnrollmentContext,
  getMyRecommendations,
  submitMyElections,
  type DependentInput,
  type EnrollmentContext,
  type ElectionItemPayload,
  type PlanRecommendation,
  type Tier,
  type WizardAnswers,
  type WizardCoverage,
  type WizardPriority,
  type WizardRx,
  type WizardUsage,
} from "@/api/benefits";

type Step =
  | "coverage"
  | "usage"
  | "rx"
  | "priority"
  | "recommendations"
  | "dependents"
  | "review"
  | "done";

const STEP_ORDER: Step[] = [
  "coverage",
  "usage",
  "rx",
  "priority",
  "recommendations",
  "dependents",
  "review",
  "done",
];

const TIER_LABELS: Record<Tier, string> = {
  employee_only: "Just me",
  employee_spouse: "Me + spouse/partner",
  employee_children: "Me + children",
  family: "Me + family",
  waived: "Waived",
};

// ─────────── Coverage / usage / rx / priority option metadata ───────────
//
// Centralized here so the option cards stay terse + consistent. Icons
// are part of the friendly UX — "Just me" gets a person, "Family" gets
// a house, etc.

const COVERAGE_OPTIONS: {
  value: WizardCoverage;
  label: string;
  description: string;
  icon: typeof Users;
}[] = [
  {
    value: "just_me",
    label: "Just me",
    description: "Single coverage for one person.",
    icon: Users,
  },
  {
    value: "me_spouse",
    label: "Me + spouse / partner",
    description: "Coverage for you and your spouse or domestic partner.",
    icon: Heart,
  },
  {
    value: "me_children",
    label: "Me + children",
    description: "Coverage for you and one or more dependent children.",
    icon: Users,
  },
  {
    value: "me_family",
    label: "Me + family",
    description: "Coverage for you, your spouse/partner, and children.",
    icon: Home,
  },
];

const USAGE_OPTIONS: {
  value: WizardUsage;
  label: string;
  description: string;
}[] = [
  {
    value: "rarely",
    label: "Rarely",
    description: "1-2 times a year, mostly checkups.",
  },
  {
    value: "sometimes",
    label: "Sometimes",
    description: "Every few months, occasional prescriptions.",
  },
  {
    value: "often",
    label: "Often",
    description: "Monthly visits, ongoing treatment, specialists.",
  },
];

const RX_OPTIONS: { value: WizardRx; label: string; description: string }[] = [
  { value: "none", label: "None", description: "No regular prescriptions." },
  {
    value: "one_two",
    label: "1-2 medications",
    description: "A small number of regular prescriptions.",
  },
  {
    value: "three_plus",
    label: "3 or more",
    description: "Multiple regular prescriptions.",
  },
];

const PRIORITY_OPTIONS: {
  value: WizardPriority;
  label: string;
  description: string;
  icon: typeof Wallet;
}[] = [
  {
    value: "lower_monthly",
    label: "Lower monthly cost",
    description: "I'll pay more when I actually need care.",
    icon: Wallet,
  },
  {
    value: "predictable",
    label: "Predictable costs",
    description: "I'd rather pay more monthly and less at the doctor.",
    icon: ShieldCheck,
  },
  {
    value: "best_coverage",
    label: "Best coverage possible",
    description: "I want the lowest out-of-pocket if something happens.",
    icon: Sparkles,
  },
];


// ────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────


export function BenefitsWizardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();

  // ── Wizard state ──
  const [step, setStep] = useState<Step>("coverage");
  const [coverage, setCoverage] = useState<WizardCoverage | null>(null);
  const [usage, setUsage] = useState<WizardUsage | null>(null);
  const [rx, setRx] = useState<WizardRx | null>(null);
  const [priority, setPriority] = useState<WizardPriority | null>(null);
  const [recs, setRecs] = useState<PlanRecommendation[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [dependents, setDependents] = useState<DependentInput[]>([]);
  const [periodId, setPeriodId] = useState<string | null>(null);
  const [resolvedTier, setResolvedTier] = useState<Tier>("employee_only");

  // ── Async state ──
  const [context, setContext] = useState<EnrollmentContext | null>(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [recsLoading, setRecsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState<string | null>(null);

  // Load the enrollment context on mount so we know whether there's an
  // active period AND have the period_id handy for the submission step.
  useEffect(() => {
    let cancelled = false;
    setContextLoading(true);
    getMyEnrollmentContext()
      .then((ctx) => {
        if (cancelled) return;
        setContext(ctx);
        if (ctx.period) setPeriodId(ctx.period.id);
      })
      .catch((err) => {
        console.error("BenefitsWizardPage: context load failed", err);
        if (!cancelled) addToast("error", "Failed to load enrollment info");
      })
      .finally(() => {
        if (!cancelled) setContextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  const currentStepIndex = STEP_ORDER.indexOf(step);
  const totalQuestionSteps = 4; // coverage, usage, rx, priority

  // Pre-compute the answers payload — null when any question is
  // unanswered. Used to gate the "load recommendations" call.
  const answers: WizardAnswers | null = useMemo(() => {
    if (!coverage || !usage || !rx || !priority) return null;
    return { coverage, usage, rx, priority };
  }, [coverage, usage, rx, priority]);

  // When we land on the recommendations step, ask the server for ranked
  // plans. We deliberately re-fetch each time the user advances rather
  // than caching — so if they go back and change an answer, the recs
  // refresh on the next forward step.
  const loadRecommendations = useCallback(async () => {
    if (!answers) return;
    setRecsLoading(true);
    try {
      const res = await getMyRecommendations(answers);
      setRecs(res.recommendations);
      setResolvedTier(res.tier);
      if (res.period?.id) setPeriodId(res.period.id);
      setReason(res.reason ?? null);
      // Auto-select the top recommendation so the "Select this plan"
      // button has a default state — saves a click for the typical
      // happy path while still letting users pick differently.
      if (res.recommendations.length > 0) {
        setSelectedPlanId(res.recommendations[0].plan_id);
      }
    } catch (err) {
      console.error("BenefitsWizardPage: recommendations failed", err);
      addToast("error", "Couldn't load plan recommendations");
    } finally {
      setRecsLoading(false);
    }
  }, [answers, addToast]);

  const goNext = () => {
    const nextIdx = currentStepIndex + 1;
    if (nextIdx >= STEP_ORDER.length) return;
    const next = STEP_ORDER[nextIdx];
    // If we just answered the priority question, fetch recs before
    // showing the recommendations screen so it doesn't flash a spinner
    // longer than needed (the API call is in-flight during the
    // animated transition).
    if (next === "recommendations") {
      loadRecommendations();
    }
    // Skip dependents if they picked "just me" — nothing to add.
    if (next === "dependents" && coverage === "just_me") {
      setStep("review");
      return;
    }
    setStep(next);
  };

  const goBack = () => {
    const prevIdx = currentStepIndex - 1;
    if (prevIdx < 0) {
      navigate(-1);
      return;
    }
    // Mirror the skip-dependents logic on the way back.
    const prev = STEP_ORDER[prevIdx];
    if (prev === "dependents" && coverage === "just_me") {
      setStep("recommendations");
      return;
    }
    setStep(prev);
  };

  const selectedPlan = recs.find((r) => r.plan_id === selectedPlanId) ?? null;

  const handleConfirm = async () => {
    if (!periodId || !selectedPlan) return;
    setSubmitting(true);
    try {
      const payload: ElectionItemPayload[] = [
        {
          plan_id: selectedPlan.plan_id,
          plan_type: selectedPlan.plan_type,
          tier: resolvedTier,
          dependents: dependents.length ? dependents : undefined,
        },
      ];
      await submitMyElections(periodId, payload);
      setStep("done");
    } catch (err) {
      // The 400 path is a validation issue (e.g. trying to submit
      // outside an active period). Surface the backend detail.
      const e = err as {
        response?: { data?: { detail?: string } };
      };
      const detail = e?.response?.data?.detail;
      addToast(
        "error",
        typeof detail === "string"
          ? detail
          : "Couldn't finalize your enrollment. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────

  // Loading shimmer for the initial context fetch.
  if (contextLoading) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-4">
        <div className="h-12 w-72 bg-surface-2 rounded-lg animate-pulse mb-4" />
        <div className="h-64 bg-surface-2 rounded-2xl animate-pulse" />
      </div>
    );
  }

  // No active period — friendly empty state instead of a crash.
  if (!context?.period && step !== "done") {
    return (
      <div className="max-w-2xl mx-auto py-16 px-4 text-center">
        <ShieldCheck className="h-12 w-12 mx-auto text-accent mb-4" />
        <h1 className="text-2xl font-semibold text-text-primary mb-2">
          No open enrollment right now
        </h1>
        <p className="text-text-muted mb-6">
          You'll be able to pick or change your benefits when open
          enrollment starts. If you had a life event (marriage, birth,
          loss of coverage), you can report it to open a 30-day window.
        </p>
        <div className="flex justify-center gap-3">
          <Button variant="secondary" onClick={() => navigate("/benefits")}>
            Back to Benefits
          </Button>
          <Button onClick={() => navigate("/benefits/life-event")}>
            Report a life event
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-bg">
      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Progress + back affordance */}
        {step !== "done" && (
          <div className="mb-8">
            <button
              onClick={goBack}
              className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-text-primary mb-6"
            >
              <ArrowLeft className="h-4 w-4" />
              {currentStepIndex === 0 ? "Back to benefits" : "Back"}
            </button>
            <ProgressBar
              current={Math.min(currentStepIndex, totalQuestionSteps)}
              total={totalQuestionSteps}
            />
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === "coverage" && (
            <QuestionStep
              key="coverage"
              eyebrow="Step 1 of 4"
              title="Who are you covering?"
              subtitle="We'll use this to find plans priced for your situation."
            >
              <OptionGrid
                options={COVERAGE_OPTIONS}
                value={coverage}
                onChange={setCoverage}
              />
              <StepActions onNext={goNext} canNext={coverage !== null} />
            </QuestionStep>
          )}

          {step === "usage" && (
            <QuestionStep
              key="usage"
              eyebrow="Step 2 of 4"
              title="How often do you visit the doctor?"
              subtitle="No exact number — just roughly."
            >
              <OptionList
                options={USAGE_OPTIONS}
                value={usage}
                onChange={setUsage}
                renderIcon={() => <Stethoscope className="h-5 w-5" />}
              />
              <StepActions onNext={goNext} canNext={usage !== null} />
            </QuestionStep>
          )}

          {step === "rx" && (
            <QuestionStep
              key="rx"
              eyebrow="Step 3 of 4"
              title="Do you take any regular prescriptions?"
              subtitle="This helps us model your real-world out-of-pocket costs."
            >
              <OptionList
                options={RX_OPTIONS}
                value={rx}
                onChange={setRx}
                renderIcon={() => <Pill className="h-5 w-5" />}
              />
              <StepActions onNext={goNext} canNext={rx !== null} />
            </QuestionStep>
          )}

          {step === "priority" && (
            <QuestionStep
              key="priority"
              eyebrow="Step 4 of 4"
              title="What matters more to you?"
              subtitle="There's no wrong answer — different plans suit different priorities."
            >
              <OptionGrid
                options={PRIORITY_OPTIONS}
                value={priority}
                onChange={setPriority}
              />
              <StepActions
                onNext={goNext}
                canNext={priority !== null}
                nextLabel="See recommendations"
              />
            </QuestionStep>
          )}

          {step === "recommendations" && (
            <motion.div
              key="recommendations"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
            >
              <RecommendationsStep
                loading={recsLoading}
                recs={recs}
                selectedPlanId={selectedPlanId}
                onSelect={setSelectedPlanId}
                comparisonOpen={comparisonOpen}
                onToggleComparison={() => setComparisonOpen((v) => !v)}
                reason={reason}
                tier={resolvedTier}
              />
              <StepActions
                onNext={goNext}
                canNext={selectedPlanId !== null}
                nextLabel={
                  coverage === "just_me" ? "Review & confirm" : "Add dependents"
                }
              />
            </motion.div>
          )}

          {step === "dependents" && (
            <QuestionStep
              key="dependents"
              eyebrow="Add your dependents"
              title="Who are you adding to your plan?"
              subtitle="You can skip and add them later — just make sure to upload documents within 30 days."
            >
              <DependentsEditor
                dependents={dependents}
                onChange={setDependents}
                coverage={coverage}
              />
              <StepActions
                onNext={goNext}
                canNext
                nextLabel="Review & confirm"
              />
            </QuestionStep>
          )}

          {step === "review" && (
            <motion.div
              key="review"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
            >
              <ReviewStep
                plan={selectedPlan}
                tier={resolvedTier}
                dependents={dependents}
                effectiveDate={context?.period?.start_date ?? null}
                submitting={submitting}
                onConfirm={handleConfirm}
                onBack={goBack}
              />
            </motion.div>
          )}

          {step === "done" && selectedPlan && (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <DoneStep
                plan={selectedPlan}
                tier={resolvedTier}
                effectiveDate={context?.period?.start_date ?? null}
                userFirstName={user?.first_name ?? null}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Sub-components — kept in-file so the wizard is one self-contained
// piece. If any of these get re-used elsewhere we extract them then.
// ────────────────────────────────────────────────────────────────────


function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = Math.min(100, ((current + 1) / total) * 100);
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 bg-surface-2 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-accent"
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>
      <span className="text-xs text-text-muted whitespace-nowrap">
        Step {Math.min(current + 1, total)} of {total}
      </span>
    </div>
  );
}


interface QuestionStepProps {
  eyebrow: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

function QuestionStep({
  eyebrow,
  title,
  subtitle,
  children,
}: QuestionStepProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.2 }}
    >
      <p className="text-xs uppercase tracking-wide text-accent font-semibold mb-2">
        {eyebrow}
      </p>
      <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-3">
        {title}
      </h1>
      {subtitle && (
        <p className="text-base text-text-muted mb-8">{subtitle}</p>
      )}
      {children}
    </motion.div>
  );
}


interface StepActionsProps {
  onNext: () => void;
  canNext: boolean;
  nextLabel?: string;
}

function StepActions({
  onNext,
  canNext,
  nextLabel = "Continue",
}: StepActionsProps) {
  return (
    <div className="mt-10 flex justify-end">
      <Button onClick={onNext} disabled={!canNext}>
        {nextLabel}
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}


/** Two-column grid for ≤4 options with an icon + description. */
function OptionGrid<T extends string>({
  options,
  value,
  onChange,
}: {
  options: {
    value: T;
    label: string;
    description: string;
    icon: typeof Users;
  }[];
  value: T | null;
  onChange: (v: T) => void;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {options.map((opt) => {
        const Icon = opt.icon;
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`relative text-left rounded-2xl border-2 px-5 py-4 transition-all ${
              selected
                ? "border-accent bg-accent/5 shadow-md"
                : "border-border bg-surface hover:border-accent/40"
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`rounded-lg p-2 ${
                  selected ? "bg-accent text-white" : "bg-accent/10 text-accent"
                }`}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-text-primary">{opt.label}</p>
                <p className="text-sm text-text-muted mt-0.5">
                  {opt.description}
                </p>
              </div>
              {selected && (
                <Check className="h-5 w-5 text-accent shrink-0 mt-1" />
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}


/** Stacked-list variant for 3 options without per-option icons. */
function OptionList<T extends string>({
  options,
  value,
  onChange,
  renderIcon,
}: {
  options: { value: T; label: string; description: string }[];
  value: T | null;
  onChange: (v: T) => void;
  renderIcon?: () => React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`w-full text-left rounded-2xl border-2 px-5 py-4 transition-all ${
              selected
                ? "border-accent bg-accent/5 shadow-md"
                : "border-border bg-surface hover:border-accent/40"
            }`}
          >
            <div className="flex items-start gap-3">
              {renderIcon && (
                <div
                  className={`rounded-lg p-2 mt-0.5 ${
                    selected
                      ? "bg-accent text-white"
                      : "bg-accent/10 text-accent"
                  }`}
                >
                  {renderIcon()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-text-primary">{opt.label}</p>
                <p className="text-sm text-text-muted mt-0.5">
                  {opt.description}
                </p>
              </div>
              {selected && (
                <Check className="h-5 w-5 text-accent shrink-0 mt-1" />
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}


// ──────────── Recommendations + comparison ────────────


interface RecommendationsStepProps {
  loading: boolean;
  recs: PlanRecommendation[];
  selectedPlanId: string | null;
  onSelect: (id: string) => void;
  comparisonOpen: boolean;
  onToggleComparison: () => void;
  reason: string | null;
  tier: Tier;
}

function RecommendationsStep({
  loading,
  recs,
  selectedPlanId,
  onSelect,
  comparisonOpen,
  onToggleComparison,
  reason,
  tier,
}: RecommendationsStepProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-80 bg-surface-2 rounded animate-pulse" />
        <div className="h-48 bg-surface-2 rounded-2xl animate-pulse" />
        <div className="h-48 bg-surface-2 rounded-2xl animate-pulse" />
      </div>
    );
  }
  if (recs.length === 0) {
    const msg =
      reason === "no_active_period"
        ? "Open enrollment isn't running right now."
        : reason === "no_eligible_plans"
        ? "Your organization hasn't configured medical plans for this enrollment yet."
        : "No plans to recommend.";
    return (
      <div className="text-center py-12">
        <HelpCircle className="h-12 w-12 text-text-muted mx-auto mb-4" />
        <p className="text-text-muted">{msg}</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-accent font-semibold mb-2">
        Your recommendations
      </p>
      <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-3">
        Here&apos;s what we recommend for you
      </h1>
      <p className="text-base text-text-muted mb-6">
        Ranked by estimated annual cost for {TIER_LABELS[tier].toLowerCase()},
        based on your answers.
      </p>

      <div className="flex justify-end mb-4">
        <button
          onClick={onToggleComparison}
          className="text-sm font-medium text-accent hover:underline"
        >
          {comparisonOpen
            ? "Hide side-by-side comparison"
            : "Compare plans side by side →"}
        </button>
      </div>

      {comparisonOpen && (
        <ComparisonTable recs={recs} />
      )}

      <div className="space-y-4">
        {recs.map((rec) => (
          <PlanCard
            key={rec.plan_id}
            rec={rec}
            selected={selectedPlanId === rec.plan_id}
            onSelect={() => onSelect(rec.plan_id)}
          />
        ))}
      </div>
    </div>
  );
}


function PlanCard({
  rec,
  selected,
  onSelect,
}: {
  rec: PlanRecommendation;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      className={`relative rounded-2xl border-2 p-5 transition-all ${
        rec.is_recommended
          ? "border-accent bg-accent/[0.03] shadow-md"
          : "border-border bg-surface"
      } ${selected ? "ring-2 ring-accent/40" : ""}`}
    >
      {rec.is_recommended && (
        <div className="absolute -top-3 left-5 inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-white shadow">
          <Sparkles className="h-3 w-3" />
          Recommended for you
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-xl font-bold text-text-primary">
            {rec.plan_name}
          </h3>
          <p className="text-sm text-text-muted mb-3">{rec.carrier_name}</p>

          <div className="grid grid-cols-2 gap-x-6 gap-y-2 max-w-md mb-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-muted">
                Your monthly cost
              </p>
              <p className="text-2xl font-bold text-text-primary">
                {formatCurrency(rec.monthly_premium)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-text-muted">
                Estimated annual cost
              </p>
              <p className="text-2xl font-bold text-text-primary">
                {formatCurrency(rec.estimated_annual_cost)}
              </p>
              <p className="text-[11px] text-text-muted">
                (premiums + typical out-of-pocket)
              </p>
            </div>
          </div>

          <p className="text-sm text-text-primary leading-relaxed">
            {rec.is_recommended && (
              <span className="font-semibold text-accent">
                Why this plan:{" "}
              </span>
            )}
            {rec.reasoning}
          </p>
        </div>

        <div className="shrink-0">
          <Button
            onClick={onSelect}
            variant={selected ? "primary" : "secondary"}
            className="min-w-[140px]"
          >
            {selected ? (
              <>
                <Check className="h-4 w-4" />
                Selected
              </>
            ) : (
              "Select this plan"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}


/** Side-by-side table — highlights the winning value in each row. */
function ComparisonTable({ recs }: { recs: PlanRecommendation[] }) {
  if (recs.length < 2) return null;

  type Row = {
    label: string;
    valueOf: (r: PlanRecommendation) => number;
    format: (v: number) => string;
    /** lower-is-better (cost) vs higher-is-better (coverage) */
    direction: "lower" | "higher";
  };

  const rows: Row[] = [
    {
      label: "Your monthly cost",
      valueOf: (r) => r.monthly_premium,
      format: (v) => formatCurrency(v),
      direction: "lower",
    },
    {
      label: "Deductible",
      valueOf: (r) => r.details.deductible,
      format: (v) => formatCurrency(v),
      direction: "lower",
    },
    {
      label: "Out-of-pocket max",
      valueOf: (r) => r.details.oop_max,
      format: (v) => formatCurrency(v),
      direction: "lower",
    },
    {
      label: "PCP copay",
      valueOf: (r) => r.details.pcp_copay,
      format: (v) => formatCurrency(v),
      direction: "lower",
    },
    {
      label: "Specialist copay",
      valueOf: (r) => r.details.specialist_copay,
      format: (v) => formatCurrency(v),
      direction: "lower",
    },
    {
      label: "ER copay",
      valueOf: (r) => r.details.er_copay,
      format: (v) => formatCurrency(v),
      direction: "lower",
    },
    {
      label: "Rx (generic)",
      valueOf: (r) => r.details.rx_copay,
      format: (v) => formatCurrency(v),
      direction: "lower",
    },
    {
      label: "Est. annual cost",
      valueOf: (r) => r.estimated_annual_cost,
      format: (v) => formatCurrency(v),
      direction: "lower",
    },
  ];

  // For each row, find the "winning" plan_id so we can highlight that
  // cell in green. Ties don't get highlighted.
  function winnerId(r: Row): string | null {
    let best = r.valueOf(recs[0]);
    let id: string | null = recs[0].plan_id;
    let tie = false;
    for (let i = 1; i < recs.length; i++) {
      const v = r.valueOf(recs[i]);
      if (r.direction === "lower" ? v < best : v > best) {
        best = v;
        id = recs[i].plan_id;
        tie = false;
      } else if (v === best) {
        tie = true;
      }
    }
    return tie ? null : id;
  }

  return (
    <div className="mb-6 overflow-x-auto rounded-2xl border border-border bg-surface">
      <table className="w-full text-sm">
        <thead className="bg-surface-2 text-left text-xs uppercase text-text-muted">
          <tr>
            <th className="px-4 py-3 font-medium">&nbsp;</th>
            {recs.map((r) => (
              <th key={r.plan_id} className="px-4 py-3 font-medium">
                <div className="text-text-primary font-bold">{r.plan_name}</div>
                {r.is_recommended && (
                  <div className="text-[10px] text-accent font-semibold mt-0.5">
                    RECOMMENDED
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const winId = winnerId(row);
            return (
              <tr key={row.label} className="border-t border-border">
                <td className="px-4 py-2 text-text-muted">{row.label}</td>
                {recs.map((r) => {
                  const v = row.valueOf(r);
                  const win = winId === r.plan_id;
                  return (
                    <td
                      key={r.plan_id}
                      className={`px-4 py-2 font-medium ${
                        win
                          ? "text-success font-bold bg-success/5"
                          : "text-text-primary"
                      }`}
                    >
                      {row.format(v)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {/* Savings row — only for the top plan. */}
          {recs[0].savings_vs_next && recs[0].savings_vs_next > 0 && (
            <tr className="border-t border-border bg-surface-2">
              <td className="px-4 py-2 font-semibold text-text-primary">
                Your savings (top pick)
              </td>
              <td className="px-4 py-2 font-bold text-success">
                {formatCurrency(recs[0].savings_vs_next)}/yr
              </td>
              {recs.slice(1).map((r) => (
                <td key={r.plan_id} className="px-4 py-2 text-text-muted">
                  —
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


// ──────────── Dependents editor ────────────


function DependentsEditor({
  dependents,
  onChange,
  coverage,
}: {
  dependents: DependentInput[];
  onChange: (d: DependentInput[]) => void;
  coverage: WizardCoverage | null;
}) {
  const addDep = () =>
    onChange([
      ...dependents,
      {
        name: "",
        relationship:
          coverage === "me_spouse"
            ? "spouse"
            : coverage === "me_children"
            ? "child"
            : "spouse",
      },
    ]);

  const updateDep = (idx: number, patch: Partial<DependentInput>) => {
    const next = [...dependents];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeDep = (idx: number) => {
    onChange(dependents.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4">
      {dependents.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-border bg-surface px-6 py-10 text-center">
          <Users className="h-8 w-8 text-text-muted mx-auto mb-3" />
          <p className="text-text-muted mb-4">
            No dependents added yet. You can add them later if you don&apos;t
            have documents handy.
          </p>
          <Button onClick={addDep} variant="secondary">
            Add a dependent
          </Button>
        </div>
      )}
      {dependents.map((d, idx) => (
        <div
          key={idx}
          className="rounded-2xl border border-border bg-surface p-4 space-y-3"
        >
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-text-primary">
              Dependent {idx + 1}
            </h4>
            <button
              onClick={() => removeDep(idx)}
              className="text-text-muted hover:text-danger p-1"
              aria-label="Remove dependent"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Full name"
              value={d.name}
              onChange={(e) => updateDep(idx, { name: e.target.value })}
              required
            />
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Relationship
              </label>
              <select
                value={d.relationship ?? "spouse"}
                onChange={(e) =>
                  updateDep(idx, {
                    relationship: e.target.value as
                      | "spouse"
                      | "child"
                      | "domestic_partner"
                      | "other",
                  })
                }
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
              >
                <option value="spouse">Spouse</option>
                <option value="domestic_partner">Domestic partner</option>
                <option value="child">Child</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Date of birth"
              type="date"
              value={d.dob ?? ""}
              onChange={(e) => updateDep(idx, { dob: e.target.value })}
            />
            <Input
              label="SSN (optional)"
              value={d.ssn ?? ""}
              onChange={(e) => updateDep(idx, { ssn: e.target.value })}
              placeholder="Encrypted at rest"
            />
          </div>
        </div>
      ))}
      {dependents.length > 0 && (
        <button
          onClick={addDep}
          className="w-full rounded-2xl border-2 border-dashed border-border px-4 py-3 text-sm font-medium text-accent hover:bg-accent/5"
        >
          + Add another dependent
        </button>
      )}
    </div>
  );
}


// ──────────── Review + done ────────────


function ReviewStep({
  plan,
  tier,
  dependents,
  effectiveDate,
  submitting,
  onConfirm,
  onBack,
}: {
  plan: PlanRecommendation | null;
  tier: Tier;
  dependents: DependentInput[];
  effectiveDate: string | null;
  submitting: boolean;
  onConfirm: () => void;
  onBack: () => void;
}) {
  if (!plan) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-accent font-semibold mb-2">
        Review &amp; confirm
      </p>
      <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
        Let&apos;s make sure everything looks right.
      </h1>

      <div className="rounded-2xl border border-border bg-surface p-6 space-y-4">
        <ReviewRow label="Plan selected" value={plan.plan_name} />
        <ReviewRow label="Carrier" value={plan.carrier_name} />
        <ReviewRow label="Coverage tier" value={TIER_LABELS[tier]} />
        <ReviewRow
          label="Your monthly cost"
          value={formatCurrency(plan.monthly_premium)}
          emphasis
        />
        <ReviewRow
          label="Estimated annual cost"
          value={formatCurrency(plan.estimated_annual_cost)}
        />
        {dependents.length > 0 && (
          <ReviewRow
            label="Dependents"
            value={dependents
              .map((d) =>
                d.relationship
                  ? `${d.name} (${d.relationship.replace("_", " ")})`
                  : d.name,
              )
              .join(", ")}
          />
        )}
        {effectiveDate && (
          <ReviewRow
            label="Effective date"
            value={new Date(effectiveDate).toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          />
        )}
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
        <Button variant="secondary" onClick={onBack} disabled={submitting}>
          Go back and change
        </Button>
        <Button onClick={onConfirm} isLoading={submitting}>
          Confirm enrollment
        </Button>
      </div>
    </div>
  );
}

function ReviewRow({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="flex justify-between items-baseline gap-4">
      <span className="text-sm text-text-muted">{label}</span>
      <span
        className={`text-right ${
          emphasis
            ? "text-xl font-bold text-text-primary"
            : "text-base font-medium text-text-primary"
        }`}
      >
        {value}
      </span>
    </div>
  );
}


function DoneStep({
  plan,
  tier,
  effectiveDate,
  userFirstName,
}: {
  plan: PlanRecommendation;
  tier: Tier;
  effectiveDate: string | null;
  userFirstName: string | null;
}) {
  const navigate = useNavigate();
  const effDateLabel = effectiveDate
    ? new Date(effectiveDate).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;
  return (
    <div className="text-center py-8">
      <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-success/10 mb-6">
        <CheckCircle2 className="h-12 w-12 text-success" />
      </div>
      <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-3">
        {userFirstName ? `Nice work, ${userFirstName}!` : "You're enrolled!"}
      </h1>
      <p className="text-lg text-text-muted mb-6">
        You&apos;re enrolled in <strong>{plan.plan_name}</strong>
        {effDateLabel ? ` starting ${effDateLabel}` : ""}.
      </p>

      <div className="max-w-md mx-auto rounded-2xl border border-border bg-surface p-6 text-left space-y-3">
        <ReviewRow
          label="Coverage tier"
          value={TIER_LABELS[tier]}
        />
        <ReviewRow
          label="Monthly premium"
          value={`${formatCurrency(plan.monthly_premium)} (deducted from payroll)`}
          emphasis
        />
        {effDateLabel && (
          <ReviewRow label="Coverage starts" value={effDateLabel} />
        )}
      </div>

      <p className="text-sm text-text-muted mt-6 max-w-md mx-auto">
        Your benefits ID card will be available within 5-7 business days.
        We&apos;ve emailed you a confirmation with all the details.
      </p>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          variant="secondary"
          onClick={() => navigate("/benefits")}
        >
          <Download className="h-4 w-4" />
          View my benefits
        </Button>
        <Button onClick={() => navigate("/")}>Done</Button>
      </div>
    </div>
  );
}

